import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { LeagueChip, LeagueLogo } from "@/components/LeagueLogo";
import {
  Search, Star, User, Activity, Award, Trophy, Loader2, AlertCircle, X,
} from "lucide-react";
import { toast } from "sonner";

const LEAGUES = ["All", "EPL", "NBA"];

export default function Players() {
  const [league, setLeague] = useState("EPL");
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [list, setList] = useState([]);
  const [top, setTop] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [syncing, setSyncing] = useState(false);

  // debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setLoading(true);
    const params = { limit: 120 };
    if (league !== "All") params.league = league;
    if (debounced) params.search = debounced;
    api.get("/players", { params }).then((r) => setList(r.data)).finally(() => setLoading(false));
  }, [league, debounced]);

  // Top performers strip (only for EPL — where we have rich stats + photos)
  useEffect(() => {
    if (league !== "EPL") { setTop([]); return; }
    api.get("/players/top/EPL", { params: { limit: 8 } })
      .then((r) => setTop(r.data))
      .catch(() => setTop([]));
  }, [league]);

  const syncLeague = async (lg) => {
    setSyncing(true);
    try {
      const { data } = await api.post(`/players/sync/${lg}`);
      const count = (data.players_upserted ?? 0) + (data.top_scorers?.players_upserted ?? 0);
      toast.success(`Synced ${count} ${lg} players`);
      // reload list
      setLeague(lg);
    } catch (e) {
      toast.error(e?.response?.data?.detail ?? `Failed to sync ${lg}`);
    } finally { setSyncing(false); }
  };

  return (
    <div className="mx-auto max-w-7xl space-y-6" data-testid="players-page">
      <PageHeader
        title="Fantasy players"
        subtitle="Cross-league player hub. EPL has rich stats + photos from API-Sports; other leagues show rosters."
        actions={
          <button onClick={() => syncLeague(league === "All" ? "EPL" : league)} disabled={syncing} className="iso-btn-outline !py-1.5 !text-xs" data-testid="players-sync-btn">
            {syncing ? <Loader2 className="size-3.5 animate-spin" /> : <Activity className="size-3.5" />}
            Sync {league === "All" ? "EPL" : league}
          </button>
        }
      />

      {/* Top performers strip */}
      {league === "EPL" && top.length > 0 && (
        <div className="iso-card p-5" data-testid="top-performers">
          <div className="mb-3 flex items-center gap-2">
            <Trophy className="size-4 text-orange" />
            <div className="font-lexend text-sm font-bold text-gray-900">Top performers</div>
            <LeagueChip code="EPL" />
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-4 xl:grid-cols-8">
            {top.map((p, i) => (
              <button
                key={p.id}
                onClick={() => setSelected(p)}
                className="group rounded-xl border border-border bg-gray-0 p-3 text-left transition-all hover:border-secondary hover:shadow-card"
                data-testid={`top-player-${i}`}
              >
                <div className="relative mx-auto h-16 w-16">
                  <div className="h-full w-full overflow-hidden rounded-full bg-gray-100">
                    {p.photo ? (
                      <img src={p.photo} alt={p.name} className="h-full w-full object-cover" draggable={false} />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-gray-400"><User className="size-6" /></div>
                    )}
                  </div>
                  <div className="absolute -top-1 -right-1 flex size-6 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground ring-2 ring-gray-0">
                    {i + 1}
                  </div>
                </div>
                <div className="mt-2 truncate text-center text-xs font-semibold text-gray-900">{p.name}</div>
                <div className="mt-0.5 flex items-center justify-center gap-1">
                  {p.team_logo && <img src={p.team_logo} alt="" className="h-3 w-3" />}
                  <div className="truncate text-[10px] text-gray-500">{p.team_name}</div>
                </div>
                <div className="mt-1 flex items-center justify-center gap-2 text-[10px]">
                  <span className="font-lexend font-bold text-secondary-dark">{p.stats?.goals ?? 0}G</span>
                  <span className="font-lexend font-bold text-gray-700">{p.stats?.assists ?? 0}A</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="iso-card flex flex-wrap items-center gap-3 p-3">
        <div className="flex items-center gap-1 rounded-lg border border-border bg-gray-0 p-1">
          {LEAGUES.map((l) => (
            <button
              key={l}
              onClick={() => setLeague(l)}
              className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-all ${
                league === l ? "bg-primary text-primary-foreground" : "text-gray-500 hover:text-gray-900"
              }`}
              data-testid={`players-league-${l.toLowerCase()}`}
            >
              {l !== "All" && <LeagueLogo code={l} size={14} />}
              {l}
            </button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[240px]">
          <Search className="pointer-events-none absolute left-3 top-2.5 size-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search players by name…"
            className="iso-input !pl-9"
            data-testid="players-search"
          />
        </div>
        <div className="text-xs text-gray-500">{list.length} results</div>
      </div>

      {/* Player grid */}
      {loading ? (
        <div className="iso-card p-12 text-center text-gray-500">
          <Loader2 className="mx-auto mb-2 size-5 animate-spin" /> Loading players…
        </div>
      ) : list.length === 0 ? (
        <EmptyState league={league} onSync={() => syncLeague(league === "All" ? "EPL" : league)} syncing={syncing} />
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {list.map((p) => (
            <PlayerCard key={p.id} player={p} onClick={() => setSelected(p)} />
          ))}
        </div>
      )}

      {selected && <PlayerModal player={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

const EmptyState = ({ league, onSync, syncing }) => (
  <div className="iso-card p-12 text-center">
    <AlertCircle className="mx-auto size-10 text-gray-300" />
    <h3 className="mt-3 font-lexend">No players synced yet</h3>
    <p className="mt-1 text-sm text-gray-500">Click sync to pull the latest {league} roster from API-Sports.</p>
    <button onClick={onSync} disabled={syncing} className="iso-btn-primary mt-4" data-testid="empty-sync-btn">
      {syncing ? <Loader2 className="size-4 animate-spin" /> : <Activity className="size-4" />}
      Sync {league}
    </button>
  </div>
);

const PlayerCard = ({ player, onClick }) => {
  const initials = (player.name || "?").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
  const isInjured = player.injured;
  return (
    <button
      onClick={onClick}
      className="iso-card flex w-full items-center gap-3 p-4 text-left transition-all hover:shadow-card-lg hover:border-secondary/40"
      data-testid={`player-card-${player.id}`}
    >
      <div className="relative size-14 shrink-0 overflow-hidden rounded-full bg-gray-100">
        {player.photo ? (
          <img src={player.photo} alt={player.name} className="h-full w-full object-cover" draggable={false} />
        ) : (
          <div className="flex h-full w-full items-center justify-center font-lexend text-sm font-bold text-gray-500">
            {initials}
          </div>
        )}
        {isInjured && (
          <div className="absolute -bottom-0.5 -right-0.5 flex size-5 items-center justify-center rounded-full bg-red text-white">
            <AlertCircle className="size-3" />
          </div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <div className="truncate text-sm font-semibold text-gray-900">{player.name}</div>
          {player.number != null && (
            <span className="text-[10px] font-mono text-gray-400">#{player.number}</span>
          )}
        </div>
        <div className="mt-0.5 flex items-center gap-1.5">
          <LeagueLogo code={player.league} size={14} />
          <span className="truncate text-[11px] text-gray-500">
            {player.team_name || player.position || "—"}
          </span>
        </div>
        {(player.stats?.goals != null || player.position) && (
          <div className="mt-1.5 flex items-center gap-2">
            {player.position && (
              <span className="iso-chip !text-[9px]">{player.position}</span>
            )}
            {player.stats?.goals != null && (
              <span className="text-[10px] font-semibold text-gray-600">
                <span className="text-secondary-dark">{player.stats.goals}G</span> · <span>{player.stats.assists ?? 0}A</span>
              </span>
            )}
            {player.stats?.rating && (
              <span className="text-[10px] font-semibold text-orange-dark">
                {parseFloat(player.stats.rating).toFixed(1)}
              </span>
            )}
          </div>
        )}
      </div>
    </button>
  );
};

const PlayerModal = ({ player, onClose }) => {
  const initials = (player.name || "?").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
  const s = player.stats || {};
  const hasStats = s.goals != null || s.assists != null || s.appearances != null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="player-modal">
      <div className="w-full max-w-md iso-card overflow-hidden">
        <div className="relative bg-gradient-to-br from-primary to-gray-800 p-6 text-white">
          <button onClick={onClose} className="absolute right-3 top-3 text-white/60 hover:text-white" data-testid="player-modal-close">
            <X className="size-5" />
          </button>
          <div className="flex items-center gap-4">
            <div className="size-20 shrink-0 overflow-hidden rounded-full bg-white/10 ring-2 ring-white/30">
              {player.photo ? (
                <img src={player.photo} alt={player.name} className="h-full w-full object-cover" draggable={false} />
              ) : (
                <div className="flex h-full w-full items-center justify-center font-lexend text-xl font-bold">{initials}</div>
              )}
            </div>
            <div className="min-w-0">
              <div className="font-lexend text-xl font-bold truncate">{player.name}</div>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-300">
                {player.team_logo && <img src={player.team_logo} alt="" className="size-4 rounded-sm bg-white" />}
                <span>{player.team_name || "—"}</span>
                {player.position && <span>· {player.position}</span>}
                {player.number != null && <span>· #{player.number}</span>}
              </div>
              {player.injured && (
                <span className="iso-chip iso-chip-red mt-2 inline-flex">
                  <AlertCircle className="size-3" /> Injured
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="p-5">
          <div className="grid grid-cols-3 gap-2 text-center">
            <Meta label="Age" value={player.age ?? "—"} />
            <Meta label="Height" value={player.height ?? "—"} />
            <Meta label="Weight" value={player.weight ?? "—"} />
            {player.nationality && <Meta label="Nationality" value={player.nationality} />}
          </div>
          {hasStats ? (
            <div className="mt-5">
              <div className="iso-label mb-2 flex items-center gap-1.5">
                <Star className="size-3" /> Season stats
              </div>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {s.appearances != null && <StatBox label="Apps" value={s.appearances} />}
                {s.goals != null && <StatBox label="Goals" value={s.goals} tone="secondary" />}
                {s.assists != null && <StatBox label="Assists" value={s.assists} />}
                {s.rating && <StatBox label="Rating" value={parseFloat(s.rating).toFixed(2)} tone="orange" />}
                {s.shots_total != null && <StatBox label="Shots" value={s.shots_total} />}
                {s.shots_on != null && <StatBox label="On target" value={s.shots_on} />}
                {s.minutes != null && <StatBox label="Minutes" value={s.minutes} />}
              </div>
            </div>
          ) : (
            <div className="mt-5 rounded-lg bg-gray-50 p-4 text-center text-xs text-gray-500">
              Stats not available for this league on the current API-Sports plan.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Meta = ({ label, value }) => (
  <div>
    <div className="iso-label !mb-0">{label}</div>
    <div className="font-lexend text-sm font-bold text-gray-900">{value}</div>
  </div>
);

const StatBox = ({ label, value, tone = "default" }) => {
  const toneMap = {
    default: "bg-gray-50 text-gray-900",
    secondary: "bg-secondary-lighter text-secondary-dark",
    orange: "bg-orange-lighter text-orange-dark",
  };
  return (
    <div className={`rounded-lg border border-border p-2 text-center ${toneMap[tone]}`}>
      <div className="text-[9px] font-semibold uppercase tracking-wider opacity-70">{label}</div>
      <div className="font-lexend text-sm font-bold">{value}</div>
    </div>
  );
};
